/*
 * Growing Zone engine
 * -------------------
 * Shared client logic for the global growing-zone selector and the per-product
 * "how it grows in your zone" display.
 *
 * Compatibility: stores the visitor's zone in the SAME `climate` cookie the
 * legacy Bamboo Finder used — base64(JSON.stringify(record)), 730-day expiry,
 * path=/ — so the two implementations are interchangeable and the value
 * transfers. Record shape: { zipcode, zone, frommintemp, tomintemp, source }.
 *
 * Suitability + height rules are a faithful port of the old
 * assets/bamboo-finder.js (bf_zone_rank / bf_set_climate). The only deliberate
 * change: when frommintemp < -20 in a marginal case the old code left the
 * height undefined (rendered NaN); we clamp to the coldest bracket (x0.4).
 *
 * Config (set by Liquid before this loads):
 *   window.GrowingZoneConfig = {
 *     zipUrl, zoneUrl,            // asset URLs for the lookup tables
 *     ipEndpoint, ipPostalField,  // optional IP geolocation auto-detect
 *     ipCountryField, autodetect
 *   }
 */
(() => {
  if (window.GrowingZone) return; // module dedup guard

  const COOKIE = 'climate';
  const COOKIE_DAYS = 730;

  const defaults = {
    zipUrl: '',
    zoneUrl: '',
    ipEndpoint: '',
    ipPostalField: 'postal',
    ipCountryField: 'country_code',
    autodetect: false,
    mapUrl: '', // optional USDA map image; '' uses the built-in zone key
    shopUrl: '', // "Shop by Your Zone" destination
  };
  const config = () => Object.assign({}, defaults, window.GrowingZoneConfig || {});

  /* ---------- cookie helpers (match legacy bamboo-finder.js) ---------- */
  function setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/; SameSite=Lax';
  }
  function getCookie(name) {
    const nameEQ = name + '=';
    return (
      document.cookie
        .split(';')
        .map((c) => c.trim())
        .find((c) => c.indexOf(nameEQ) === 0)
        ?.substring(nameEQ.length) ?? null
    );
  }
  function eraseCookie(name) {
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

  function getClimate() {
    const raw = getCookie(COOKIE);
    if (!raw) return null;
    try {
      return JSON.parse(atob(raw));
    } catch (e) {
      return null;
    }
  }
  function storeClimate(record) {
    setCookie(COOKIE, btoa(JSON.stringify(record)), COOKIE_DAYS);
    broadcast(record);
  }
  function clearClimate() {
    eraseCookie(COOKIE);
    broadcast(null);
  }

  // Visitor's 2-letter state (from ZIP lookup or IP), or null if unknown.
  function getState() {
    const c = getClimate();
    return c && c.state ? String(c.state).toUpperCase() : null;
  }

  /* ---------- static lookup tables (fetched once, cached) ---------- */
  let zipPromise;
  let zonePromise;
  const loadZips = () => (zipPromise ||= fetch(config().zipUrl).then((r) => r.json()));
  const loadZones = () => (zonePromise ||= fetch(config().zoneUrl).then((r) => r.json()));

  async function lookupZip(zip) {
    if (!/^\d{5}$/.test(zip)) return null;
    const [zips, zones] = await Promise.all([loadZips(), loadZones()]);
    const raw = zips[zip];
    if (!raw) return null;
    // Asset packs "zone|state" (e.g. "7a|GA"); tolerate a bare "zone" too.
    const parts = String(raw).split('|');
    const zone = parts[0];
    const state = parts[1] || null;
    const temps = zones[zone] || {};
    return { zipcode: zip, zone, state, frommintemp: temps.min, tomintemp: temps.max, source: 'zip' };
  }

  async function setByZip(zip) {
    const record = await lookupZip(zip);
    if (record) storeClimate(record);
    return record;
  }

  /* ---------- zone math (ported from bamboo-finder.js) ---------- */
  function rankZone(zone) {
    const match = String(zone || '')
      .trim()
      .toLowerCase()
      .match(/^(\d+)\s*([ab])?/);
    if (!match) return null;
    const num = parseInt(match[1], 10);
    if (isNaN(num)) return null;
    return num * 2 + (match[2] === 'b' ? 1 : 0);
  }
  function isZoneAbove7a(climate) {
    const rank = rankZone(climate.zone);
    if (rank !== null) return rank > 14; // 7a = 14
    return parseInt(climate.frommintemp, 10) > 0;
  }

  // Projected mature height by climate. Brackets are the original rules.
  function adjustedHeight(maxHeight, fromMinTemp) {
    const pmax = parseInt(maxHeight, 10);
    const cf = parseInt(fromMinTemp, 10);
    let real;
    if (cf >= 0) real = pmax;
    else if (cf >= -10) real = pmax * 0.7;
    else if (cf >= -20) real = pmax * 0.4;
    else real = pmax * 0.4; // legacy gap guard (old code left this undefined)
    return { realHeight: real, screenHeight: real * 0.8 };
  }

  // Full suitability verdict for a plant given the stored climate.
  // `plant` = { minimumtemperature, maxheight }; opts.handle for special cases.
  function assess(plant, opts = {}) {
    const climate = getClimate();
    if (!climate) return null;
    const pmin = parseInt(plant.minimumtemperature, 10);
    const pmax = parseInt(plant.maxheight, 10);
    const cf = parseInt(climate.frommintemp, 10);
    const handle = opts.handle || '';

    let canPlant = 'false';
    let intro = '';

    if (handle === 'rufa' && isZoneAbove7a(climate)) {
      intro = 'No. Your climate is too hot for this species.';
    } else if (cf >= pmin) {
      canPlant = 'true';
      intro = 'Yes! You can grow this species in your climate.';
    } else if (cf + 5 >= pmin) {
      canPlant = 'marginal';
      intro = 'Yes! You can grow this species in your climate.';
    } else if (cf + 10 >= pmin && pmin <= -10) {
      canPlant = 'marginal';
      intro =
        'Yes and no. Bamboo is marginal for your climate and success can vary. ' +
        'This is a suitable species to try, but please be aware that your mileage may vary.';
    } else if (cf <= -25) {
      intro =
        'No, unfortunately there are not any options for year-round outdoor planting ' +
        'in your climate. Indoor planting is still a viable option.';
    } else {
      intro = 'No, but there are other options! Try a more hardy bamboo.';
    }

    let container = '';
    if (canPlant === 'true') {
      container =
        'In your climate, this bamboo can be safely planted in the ground, or in an above-ground container.';
    } else if (canPlant === 'marginal') {
      container =
        'In your climate, it is only recommended to plant directly in the ground for this ' +
        'species. Above-ground containers do not provide the insulation required to protect ' +
        'the plant from cold weather.';
    }

    let size = '';
    let realHeight = null;
    if (canPlant !== 'false' && !isNaN(pmax)) {
      const h = adjustedHeight(pmax, cf);
      realHeight = Math.round(h.realHeight);
      size =
        `When planted in-ground, the projected maximum height for this species in your ` +
        `climate is <b>${realHeight} feet</b>.`;
    }

    return { canPlant, intro, container, size, realHeight, zone: climate.zone, climate };
  }

  /* ---------- event bus ---------- */
  function broadcast(record) {
    document.dispatchEvent(new CustomEvent('growing-zone:changed', { detail: record }));
  }
  function requestOpen() {
    document.dispatchEvent(new CustomEvent('growing-zone:open'));
  }

  /* ---------- optional IP-based auto-detect ---------- */
  async function autodetect() {
    const cfg = config();
    if (!cfg.autodetect || !cfg.ipEndpoint || getClimate()) return;
    try {
      const res = await fetch(cfg.ipEndpoint);
      if (!res.ok) return;
      const data = await res.json();
      const country = data[cfg.ipCountryField];
      if (country && String(country).toUpperCase() !== 'US') return;
      const postal = String(data[cfg.ipPostalField] || '').slice(0, 5);
      const record = await lookupZip(postal);
      if (record) {
        record.source = 'ip';
        storeClimate(record);
      }
    } catch (e) {
      /* graceful: silently fall back to manual entry */
    }
  }

  window.GrowingZone = {
    getClimate,
    getState,
    setByZip,
    clearClimate,
    lookupZip,
    assess,
    adjustedHeight,
    rankZone,
    requestOpen,
  };

  /* ====================================================================
   * <growing-zone-selector> — global header control (The Sill-style)
   *   Header trigger: pin + "Growing Zone Xa" / "Zip Code: NNNNN".
   *   Click opens a centered modal: zone map/key + "Find Your Growing Zone"
   *   + explainer + ZIP field + Confirm + "Shop by Your Zone".
   * ==================================================================== */
  const PIN_SVG =
    '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linejoin="round"><path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 ' +
    '4.7 7 11 7 11z"/><circle cx="12" cy="10" r="2.4"/></svg>';

  // Approximate USDA palette, cold (3) -> warm (11). Used for the built-in key.
  const ZONE_COLORS = {
    3: '#c9b8d8', 4: '#9fb4d8', 5: '#76c2c2', 6: '#7cc47a', 7: '#c2cf6e',
    8: '#e6d24f', 9: '#edb24e', 10: '#e88a4e', 11: '#e0655e',
  };
  function zoneKey(activeZone) {
    const active = parseInt((String(activeZone || '').match(/\d+/) || [])[0] || '0', 10);
    const rows = Object.keys(ZONE_COLORS)
      .map((z) => {
        const on = Number(z) === active ? ' is-active' : '';
        return (
          `<li class="gz-key__row${on}"><span class="gz-key__swatch" style="background:` +
          `${ZONE_COLORS[z]}"></span><span class="gz-key__name">Zone ${z}</span></li>`
        );
      })
      .join('');
    return `<p class="gz-key__title">USDA Hardiness Zones</p><ul class="gz-key">${rows}</ul>`;
  }

  class GrowingZoneSelector extends HTMLElement {
    connectedCallback() {
      this.innerHTML = `
        <button type="button" class="gz-trigger" aria-haspopup="dialog">
          <span class="gz-trigger__pin svg-wrapper" aria-hidden="true">${PIN_SVG}</span>
          <span class="gz-trigger__text">
            <span class="gz-trigger__zone"></span>
            <span class="gz-trigger__zip"></span>
          </span>
        </button>`;
      this.trigger = this.querySelector('.gz-trigger');
      this.zoneEl = this.querySelector('.gz-trigger__zone');
      this.zipEl = this.querySelector('.gz-trigger__zip');
      this.buildModal();

      this.trigger.addEventListener('click', () => this.open());
      document.addEventListener('growing-zone:changed', (e) => this.render(e.detail));
      document.addEventListener('growing-zone:open', () => this.open());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });

      this.render(getClimate());
      autodetect();
    }

    buildModal() {
      const cfg = config();
      const map = cfg.mapUrl
        ? `<img class="gz-modal__map-img" src="${cfg.mapUrl}" alt="USDA plant hardiness zone map" loading="lazy">`
        : zoneKey(null);
      this.modal = document.createElement('div');
      this.modal.className = 'gz-modal';
      this.modal.hidden = true;
      this.modal.setAttribute('role', 'dialog');
      this.modal.setAttribute('aria-modal', 'true');
      this.modal.setAttribute('aria-label', 'Find your growing zone');
      this.modal.innerHTML = `
        <div class="gz-modal__overlay" data-gz-close></div>
        <div class="gz-modal__panel">
          <button type="button" class="gz-modal__close" data-gz-close aria-label="Close">&times;</button>
          <div class="gz-modal__map">${map}</div>
          <div class="gz-modal__body">
            <h2 class="gz-modal__title">Find Your Growing Zone</h2>
            <p class="gz-modal__lede">Tell us your location so we can recommend the bamboo best suited for your climate.</p>
            <p class="gz-modal__current" hidden></p>
            <form class="gz-modal__form" novalidate>
              <input class="gz-modal__input" type="text" inputmode="numeric" autocomplete="postal-code"
                     maxlength="5" placeholder="Enter your ZIP code" aria-label="ZIP code">
              <button type="submit" class="gz-modal__confirm button">Confirm</button>
            </form>
            <p class="gz-modal__msg" role="status"></p>
            ${cfg.shopUrl ? `<a class="gz-modal__shop" href="${cfg.shopUrl}">Shop by Your Zone &rarr;</a>` : ''}
          </div>
        </div>`;
      document.body.appendChild(this.modal);

      this.currentEl = this.modal.querySelector('.gz-modal__current');
      this.input = this.modal.querySelector('.gz-modal__input');
      this.msg = this.modal.querySelector('.gz-modal__msg');
      this.mapEl = this.modal.querySelector('.gz-modal__map');

      this.modal.querySelector('.gz-modal__form').addEventListener('submit', (e) => {
        e.preventDefault();
        this.submit();
      });
      this.input.addEventListener('input', () => {
        this.input.classList.remove('is-invalid');
        this.msg.textContent = '';
      });
      this.modal.querySelectorAll('[data-gz-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
    }

    render(climate) {
      if (climate && climate.zone) {
        this.zoneEl.textContent = 'Growing Zone ' + climate.zone;
        this.zipEl.textContent = climate.zipcode ? 'Zip Code: ' + climate.zipcode : '';
        this.trigger.classList.add('is-set');
        if (this.currentEl) {
          this.currentEl.hidden = false;
          this.currentEl.textContent = 'Growing Zone ' + climate.zone;
        }
      } else {
        this.zoneEl.textContent = 'Set Growing Zone';
        this.zipEl.textContent = '';
        this.trigger.classList.remove('is-set');
        if (this.currentEl) this.currentEl.hidden = true;
      }
      if (this.mapEl && !config().mapUrl) {
        this.mapEl.innerHTML = zoneKey(climate && climate.zone);
      }
    }

    open() {
      this.modal.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      const current = getClimate();
      if (current && current.zipcode) this.input.value = current.zipcode;
      setTimeout(() => this.input.focus(), 0);
    }
    close() {
      this.modal.hidden = true;
      document.documentElement.style.overflow = '';
    }

    async submit() {
      const zip = this.input.value.replace(/\D/g, '').slice(0, 5);
      if (zip.length !== 5) return this.fail();
      const btn = this.modal.querySelector('.gz-modal__confirm');
      if (btn) { btn.disabled = true; btn.textContent = 'Checking…'; }
      const record = await setByZip(zip);
      if (btn) { btn.disabled = false; btn.textContent = 'Confirm'; }
      if (record) this.close(); // zone set + broadcast handled by setByZip; close the modal
      else this.fail();
    }
    fail() {
      this.input.classList.add('is-invalid');
      this.msg.textContent = 'Enter a valid ZIP code';
    }
  }
  if (!customElements.get('growing-zone-selector')) {
    customElements.define('growing-zone-selector', GrowingZoneSelector);
  }

  /* ====================================================================
   * <plant-zone-fit> — per-product "how it grows in your zone"
   *   data-handle, data-min-temp, data-max-height, data-max-diameter,
   *   data-display ("full" | "compact")
   * ==================================================================== */
  class PlantZoneFit extends HTMLElement {
    connectedCallback() {
      this.display = this.dataset.display === 'compact' ? 'compact' : 'full';
      this.plant = {
        minimumtemperature: this.dataset.minTemp,
        maxheight: this.dataset.maxHeight,
        maxdiameter: this.dataset.maxDiameter,
      };
      this.handle = this.dataset.handle || '';
      document.addEventListener('growing-zone:changed', () => this.render());
      this.render();
    }

    render() {
      // Compact (cards): strict hardiness (matches the legacy "Safe / Not Safe"
      // collection behavior) + zone-adjusted height. Renders nothing if no zone.
      if (this.display === 'compact') {
        const climate = getClimate();
        if (!climate) {
          this.className = 'plant-zone-fit plant-zone-fit--compact';
          this.innerHTML = '';
          return;
        }
        const cf = parseInt(climate.frommintemp, 10);
        const min = parseInt(this.plant.minimumtemperature, 10);
        const hardy = !isNaN(min) && cf >= min;
        this.className =
          'plant-zone-fit plant-zone-fit--compact ' + (hardy ? 'plant-zone-fit--true' : 'plant-zone-fit--false');
        if (hardy) {
          const h = window.GrowingZone.adjustedHeight(this.plant.maxheight, cf);
          this.innerHTML = `<span class="plant-zone-fit__chip">↕ ~<b>${Math.round(h.realHeight)} ft</b> in Zone ${climate.zone}</span>`;
        } else {
          this.innerHTML = `<span class="plant-zone-fit__chip">Not hardy in Zone ${climate.zone}</span>`;
        }
        return;
      }

      // Full (PDP): the complete verdict via the ported rules.
      const result = window.GrowingZone.assess(this.plant, { handle: this.handle });
      if (!result) {
        this.className = 'plant-zone-fit';
        this.innerHTML = `<button type="button" class="plant-zone-fit__prompt" data-gz-open>📍 <span>See how this grows in <u>your zone</u></span></button>`;
        const b = this.querySelector('[data-gz-open]');
        if (b) b.addEventListener('click', () => window.GrowingZone.requestOpen());
        return;
      }
      this.className = 'plant-zone-fit plant-zone-fit--' + result.canPlant;
      this.innerHTML = `
        <p class="plant-zone-fit__intro">${result.intro}</p>
        ${result.size ? `<p class="plant-zone-fit__size">${result.size}</p>` : ''}
        ${result.container ? `<p class="plant-zone-fit__container">${result.container}</p>` : ''}
        <button type="button" class="plant-zone-fit__change" data-gz-open>Zone ${result.zone} — change</button>`;
      const b = this.querySelector('[data-gz-open]');
      if (b) b.addEventListener('click', () => window.GrowingZone.requestOpen());
    }
  }
  if (!customElements.get('plant-zone-fit')) {
    customElements.define('plant-zone-fit', PlantZoneFit);
  }

  /* ====================================================================
   * <plant-meta-summary> — header metadata; zone-adjusts the mature height
   *   in place (only when the visitor's zone is hardy for the plant), using
   *   the same height rules. Falls back to the species' max height otherwise.
   * ==================================================================== */
  class PlantMetaSummary extends HTMLElement {
    connectedCallback() {
      this.heightEl = this.querySelector('[data-key="height"]');
      this.plantingEl = this.querySelector('[data-key="planting"]');
      this.baseHeight = this.heightEl ? this.heightEl.innerHTML : null;
      document.addEventListener('growing-zone:changed', () => this.update());
      // the zone label is a button — click it to open the zip/zone modal
      this.addEventListener('click', (e) => {
        if (!e.target.closest('.plant-meta__zone--btn')) return;
        e.preventDefault();
        if (window.GrowingZone && typeof window.GrowingZone.requestOpen === 'function') {
          window.GrowingZone.requestOpen();
        } else {
          document.dispatchEvent(new CustomEvent('growing-zone:open'));
        }
      });
      this.update();
    }
    update() {
      if (!this.heightEl) return;
      const climate = getClimate();
      const max = this.dataset.maxHeight;
      const min = parseInt(this.dataset.minTemp, 10);
      let label = null;
      if (climate && max) {
        const cf = parseInt(climate.frommintemp, 10);
        if (isNaN(min) || cf >= min) {
          const h = window.GrowingZone.adjustedHeight(max, cf);
          label =
            `~${Math.round(h.realHeight)} ft <button type="button" class="plant-meta__zone plant-meta__zone--btn" aria-label="Change your growing zone">in Zone ${climate.zone}</button>`;
        }
      }
      if (label === null) {
        label =
          `${this.baseHeight} <button type="button" class="plant-meta__zone plant-meta__zone--btn" aria-label="Set your growing zone">set your zone</button>`;
      }
      this.heightEl.innerHTML = label;
      this.updatePlanting();
    }
    // Zone-aware in-ground / container safety, reusing the canonical verdict.
    updatePlanting() {
      if (!this.plantingEl) return;
      const verdict = window.GrowingZone.assess(
        { minimumtemperature: this.dataset.minTemp, maxheight: this.dataset.maxHeight },
        { handle: this.dataset.handle }
      );
      if (!verdict) {
        this.plantingEl.hidden = true;
        return;
      }
      const states = {
        true: { cls: '', icon: '✓', text: 'Safe to plant <b>in-ground or in a container</b>' },
        marginal: {
          cls: 'plant-meta__planting--marginal',
          icon: '✓',
          text: 'Safe <b>in-ground</b> — outdoor containers aren’t insulated enough here',
        },
        false: {
          cls: 'plant-meta__planting--false',
          icon: '○',
          text: 'Not hardy outdoors in your zone — <b>grow it indoors</b>',
        },
      };
      const s = states[verdict.canPlant] || states.false;
      this.plantingEl.className = ('plant-meta__planting ' + s.cls).trim();
      this.plantingEl.innerHTML =
        `<span class="plant-meta__planting-icon" aria-hidden="true">${s.icon}</span>` +
        `<span class="plant-meta__planting-text">${s.text} <button type="button" class="plant-meta__zone--btn" aria-label="Change your growing zone">in Zone ${verdict.zone}</button></span>`;
      this.plantingEl.hidden = false;
    }
  }
  if (!customElements.get('plant-meta-summary')) {
    customElements.define('plant-meta-summary', PlantMetaSummary);
  }

  /* ====================================================================
   * <shield-zone-fit> — validate / upsell the Bamboo Shield barrier size for
   *   the visitor's zone. Conservative mapping: zones 4–6 → 60 mil, 7–8 → 80,
   *   9+ → 100. data-mil = current product's mil; data-url60/80/100 = barrier
   *   product URLs. Validates when the viewed size meets the zone minimum,
   *   otherwise gently upsells to the recommended size.
   * ==================================================================== */
  class ShieldZoneFit extends HTMLElement {
    connectedCallback() {
      this.mil = parseInt(this.dataset.mil, 10);
      document.addEventListener('growing-zone:changed', () => this.render());
      this.addEventListener('click', (e) => {
        if (!e.target.closest('.js-gz-open')) return;
        e.preventDefault();
        if (window.GrowingZone && typeof window.GrowingZone.requestOpen === 'function') {
          window.GrowingZone.requestOpen();
        } else {
          document.dispatchEvent(new CustomEvent('growing-zone:open'));
        }
      });
      this.render();
    }
    render() {
      const climate = getClimate();
      if (!climate || !climate.zone || isNaN(this.mil)) {
        this.className = 'shield-zone-fit';
        this.innerHTML =
          '<p class="shield-zone-fit__line"><button type="button" class="shield-zone-fit__prompt js-gz-open">📍 Set your zone to size your barrier</button></p>';
        return;
      }
      const z = parseInt(climate.zone, 10);
      const rec = z <= 6 ? 60 : z <= 8 ? 80 : 100;
      const depth = { 60: '24 in', 80: '30 in', 100: '36 in' }[rec];
      if (this.mil < rec) {
        const url = this.dataset['url' + rec] || '#';
        this.className = 'shield-zone-fit shield-zone-fit--upsell';
        this.innerHTML =
          '<p class="shield-zone-fit__line"><span class="shield-zone-fit__icon" aria-hidden="true">!</span>' +
          `<span>For <b>Zone ${climate.zone}</b>, we recommend at least <b>${rec} mil × ${depth} deep</b>. ` +
          `<a class="shield-zone-fit__cta" href="${url}">Step up to ${rec} mil →</a>` +
          `<button type="button" class="shield-zone-fit__change js-gz-open">change zone</button></span></p>`;
      } else {
        this.className = 'shield-zone-fit shield-zone-fit--ok';
        var msg =
          this.mil === rec
            ? `<b>${this.mil} mil</b> is the right barrier for <b>Zone ${climate.zone}</b> — strong enough for your climate.`
            : `<b>${this.mil} mil</b> is more than <b>Zone ${climate.zone}</b> needs — ${rec} mil is plenty here.`;
        this.innerHTML =
          '<p class="shield-zone-fit__line"><span class="shield-zone-fit__icon" aria-hidden="true">✓</span>' +
          `<span>${msg} ` +
          `<button type="button" class="shield-zone-fit__change js-gz-open">Zone ${climate.zone} · change</button></span></p>`;
      }
    }
  }
  if (!customElements.get('shield-zone-fit')) {
    customElements.define('shield-zone-fit', ShieldZoneFit);
  }

  /* ====================================================================
   * <shield-size-rec> — collection-page callout highlighting the recommended
   *   Bamboo Shield size for the visitor's zone (same mapping as
   *   <shield-zone-fit>). Prompts for a ZIP when the zone is unknown, and
   *   highlights the matching row in the static size-guide table if present.
   * ==================================================================== */
  class ShieldSizeRec extends HTMLElement {
    connectedCallback() {
      this._onZone = () => this.render();
      document.addEventListener('growing-zone:changed', this._onZone);
      this.addEventListener('click', (e) => {
        if (!e.target.closest('.js-gz-open')) return;
        e.preventDefault();
        if (window.GrowingZone && typeof window.GrowingZone.requestOpen === 'function') window.GrowingZone.requestOpen();
        else document.dispatchEvent(new CustomEvent('growing-zone:open'));
      });
      this.render();
    }
    disconnectedCallback() { document.removeEventListener('growing-zone:changed', this._onZone); }
    highlightRow(rec) {
      const table = document.querySelector('.size-guide__table');
      if (!table) return;
      const re = new RegExp('\\b' + rec + '\\s*mil', 'i');
      table.querySelectorAll('.size-guide__row').forEach((row) => {
        row.classList.toggle('size-guide__row--match', rec != null && !row.classList.contains('size-guide__row--head') && re.test(row.textContent));
      });
    }
    render() {
      const climate = getClimate();
      if (!climate || !climate.zone) {
        this.className = 'shield-size-rec';
        this.innerHTML =
          '<p class="shield-size-rec__line"><span class="shield-size-rec__icon" aria-hidden="true">📍</span>' +
          '<span>Not sure which size you need? <button type="button" class="shield-size-rec__cta js-gz-open">Tell us your ZIP</button> and we’ll recommend the right barrier for your climate.</span></p>';
        this.highlightRow(null);
        return;
      }
      const z = parseInt(climate.zone, 10);
      const rec = z <= 6 ? 60 : z <= 8 ? 80 : 100;
      const depth = { 60: '24 in', 80: '30 in', 100: '36 in' }[rec];
      this.className = 'shield-size-rec shield-size-rec--known';
      this.innerHTML =
        '<p class="shield-size-rec__line"><span class="shield-size-rec__icon" aria-hidden="true">✓</span>' +
        `<span>For your area &mdash; <b>Zone ${climate.zone}</b> &mdash; we recommend at least a <b>${rec} mil &times; ${depth} deep</b> barrier.` +
        `<button type="button" class="shield-size-rec__change js-gz-open">change zone</button></span></p>`;
      this.highlightRow(rec);
    }
  }
  if (!customElements.get('shield-size-rec')) {
    customElements.define('shield-size-rec', ShieldSizeRec);
  }

  /* ====================================================================
   * <region-gate> — show/hide wrapped content by the visitor's state.
   *   data-allowed: comma-separated 2-letter states (empty = everywhere)
   *   data-behavior: "notice" (show fallback when blocked) | "hide"
   *   Optimistic: shows content until we KNOW the state is disallowed, so
   *   visitors whose location is unknown still see it (gate at cart later).
   * ==================================================================== */
  class RegionGate extends HTMLElement {
    connectedCallback() {
      this.allowed = (this.dataset.allowed || '')
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      this.behavior = this.dataset.behavior || 'notice';
      this.content = this.querySelector('.region-gate__content');
      this.notice = this.querySelector('.region-gate__notice');
      document.addEventListener('growing-zone:changed', () => this.update());
      this.update();
    }
    update() {
      if (!this.allowed.length) return this.allow();
      const state = window.GrowingZone.getState();
      if (!state || this.allowed.includes(state)) this.allow();
      else this.block();
    }
    allow() {
      if (this.content) this.content.hidden = false;
      if (this.notice) this.notice.hidden = true;
    }
    block() {
      if (this.content) this.content.hidden = true;
      if (this.notice) this.notice.hidden = this.behavior !== 'notice';
    }
  }
  if (!customElements.get('region-gate')) {
    customElements.define('region-gate', RegionGate);
  }

  /* ====================================================================
   * Collection "Shop by Your Zone" filter
   *   Client-side hide/show of cards by hardiness (strict: zone min temp
   *   >= plant min temp). Reads data-min-temp from the per-card
   *   <plant-zone-fit>. Note: only filters the current page of results.
   * ==================================================================== */
  function initCollectionZoneFilter() {
    const grid = document.querySelector('.product-grid');
    if (!grid || grid.dataset.gzFilterReady) return;
    // Only on species/plant collections: skip if the collection opted out
    // (data-zone-filter="false"), or if no card on the page carries plant
    // hardiness (shield / fertilizer / poles etc. have none to filter by).
    const host = grid.closest('[data-zone-filter]');
    if (host && host.getAttribute('data-zone-filter') === 'false') return;
    const hasHardiness = !!grid.querySelector('.product-grid__item plant-zone-fit[data-min-temp]:not([data-min-temp=""])');
    if (!hasHardiness) return;
    grid.dataset.gzFilterReady = '1';

    let filtered = new URLSearchParams(location.search).has('shop_zone');
    const banner = document.createElement('div');
    banner.className = 'gz-zone-filter';
    banner.hidden = true;
    grid.parentNode.insertBefore(banner, grid);

    function apply() {
      const climate = getClimate();
      const items = grid.querySelectorAll('.product-grid__item');
      if (!climate) {
        items.forEach((i) => (i.style.display = ''));
        banner.hidden = true;
        return;
      }
      const cf = parseInt(climate.frommintemp, 10);
      let hardy = 0;
      let classified = 0;
      items.forEach((item) => {
        const fit = item.querySelector('plant-zone-fit');
        const raw = fit && fit.dataset.minTemp;
        if (raw == null || raw === '') {
          item.style.display = ''; // unknown hardiness -> always show
          return;
        }
        classified++;
        const ok = cf >= parseInt(raw, 10);
        if (ok) hardy++;
        item.style.display = filtered && !ok ? 'none' : '';
      });
      banner.hidden = false;
      banner.innerHTML = filtered
        ? `<span>Showing <b>${hardy}</b> of ${classified} plants hardy in <b>Zone ${climate.zone}</b> on this page.</span>` +
          `<button type="button" class="gz-zone-filter__btn">Show all</button>`
        : `<span>Filter this page to your climate.</span>` +
          `<button type="button" class="gz-zone-filter__btn">Show only plants for Zone ${climate.zone}</button>`;
      banner.querySelector('.gz-zone-filter__btn').onclick = () => {
        filtered = !filtered;
        apply();
      };
    }

    apply();
    document.addEventListener('growing-zone:changed', apply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionZoneFilter);
  } else {
    initCollectionZoneFilter();
  }
})();
